import type { TokenPair } from '@pos/auth';
import { ApiError, type AuthProvider } from './index';

export interface TokenStore {
  load(): TokenPair | null;
  save(tokens: TokenPair | null): void;
}

/** In-memory store (tests, or when persistence is not wanted). */
export function memoryTokenStore(initial: TokenPair | null = null): TokenStore {
  let t = initial;
  return { load: () => t, save: (v) => void (t = v) };
}

/**
 * Person session with rotating refresh tokens. Concurrent 401s share a single
 * refresh request, so a rotated token is never presented twice (which the
 * server would treat as theft).
 */
export class UserSession implements AuthProvider {
  private inflight: Promise<boolean> | null = null;

  constructor(
    private readonly baseUrl: string,
    private readonly store: TokenStore,
    private readonly onSignedOut?: () => void,
    private readonly doFetch: typeof fetch = (...a) => fetch(...a),
  ) {}

  getAccessToken(): string | null {
    return this.store.load()?.accessToken ?? null;
  }

  isSignedIn(): boolean {
    return this.store.load() !== null;
  }

  set(tokens: TokenPair | null): void {
    this.store.save(tokens);
  }

  renew(): Promise<boolean> {
    this.inflight ??= this.doRenew().finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  private async doRenew(): Promise<boolean> {
    const current = this.store.load();
    if (!current) return false;
    try {
      const res = await this.doFetch(`${this.baseUrl.replace(/\/$/, '')}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });
      if (!res.ok) {
        if (res.status === 401) {
          this.store.save(null);
          this.onSignedOut?.();
        }
        return false;
      }
      this.store.save((await res.json()) as TokenPair);
      return true;
    } catch {
      return false; // offline: keep tokens, try again later
    }
  }

  async signOut(): Promise<void> {
    const current = this.store.load();
    this.store.save(null);
    if (!current) return;
    try {
      await this.doFetch(`${this.baseUrl.replace(/\/$/, '')}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });
    } catch {
      // Offline: the refresh token expires on its own.
    }
  }
}

/** Device session: exchanges the device secret for short-lived access tokens. */
export class DeviceSession implements AuthProvider {
  private token: { value: string; expiresAt: number } | null = null;
  private inflight: Promise<boolean> | null = null;
  /** The server's refusal to issue a token (device disabled, secret no longer valid). */
  private refused: ApiError | null = null;

  constructor(
    private readonly baseUrl: string,
    private readonly credentials: () => { deviceId: string; secret: string } | null,
    private readonly appVersion?: string,
    private readonly doFetch: typeof fetch = (...a) => fetch(...a),
  ) {}

  async getAccessToken(): Promise<string | null> {
    if (!this.token || this.token.expiresAt - Date.now() < 30_000) await this.renew();
    return this.token?.value ?? null;
  }

  renew(): Promise<boolean> {
    this.inflight ??= this.doRenew().finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  failure(): ApiError | null {
    return this.refused;
  }

  private async doRenew(): Promise<boolean> {
    const creds = this.credentials();
    if (!creds) {
      this.refused = null;
      return false;
    }
    try {
      const res = await this.doFetch(`${this.baseUrl.replace(/\/$/, '')}/devices/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.appVersion ? { 'X-App-Version': this.appVersion } : {}),
        },
        body: JSON.stringify(creds),
      });
      if (!res.ok) {
        this.token = null;
        // Only a definite «no» is remembered; a throttled or failing server says nothing about the device.
        if (res.status === 401 || res.status === 403) {
          let err: { code?: string; message?: string } | undefined;
          try {
            err = ((await res.json()) as { error?: { code?: string; message?: string } }).error;
          } catch {
            err = undefined;
          }
          this.refused = new ApiError(
            res.status,
            err?.code ?? 'DEVICE_INACTIVE',
            err?.message ?? 'Касса отключена. Обратитесь к администратору.',
          );
        }
        return false;
      }
      const body = (await res.json()) as { accessToken: string; expiresIn: number };
      this.token = { value: body.accessToken, expiresAt: Date.now() + body.expiresIn * 1000 };
      this.refused = null;
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Browser token store for the PWA / SaaS admin. Access tokens are short-lived;
 * refresh tokens rotate with reuse detection (see API). Pages ship a strict CSP
 * and no third-party scripts, which is what keeps localStorage safe here.
 */
export function localStorageTokenStore(key: string): TokenStore {
  return {
    load() {
      try {
        const raw = localStorage.getItem(key);
        return raw ? (JSON.parse(raw) as TokenPair) : null;
      } catch {
        return null;
      }
    },
    save(tokens) {
      try {
        if (tokens) localStorage.setItem(key, JSON.stringify(tokens));
        else localStorage.removeItem(key);
      } catch {
        // storage unavailable: session lasts for this page only
      }
    },
  };
}
